import { useState } from "react";
import { toast } from "sonner";
import { X, Copy, Upload, Download, Check } from "lucide-react";
import { partnerWorkspaceApi } from "./partnerWorkspaceApi";

const METHODS = [
  { key: "paste", title: "Paste from spreadsheet", description: "Tab or comma separated: Item · Unit · Rate · Category. Rows import as Pending NJM Approval.", icon: Copy, tag: { text: "FASTEST", bg: "var(--pw-success-dot)" } },
  { key: "upload", title: "Upload Excel / CSV", description: "Requires column mapping for accurate import.", icon: Upload, tag: { text: "NEEDS ENGINEERING", bg: "var(--pw-tag-equipment)" } },
  { key: "template", title: "Download NJM template", description: "Pre-formatted with NJM's canonical categories and units.", icon: Download, tag: null },
];

/** "Item, Unit, Rate, Category" or tab-separated, one row per line — matches the design
 * bundle's own copy ("Tab or comma separated: Item · Unit · Rate · Category"). Header row
 * (if present, detected by a non-numeric 3rd column) is skipped. */
function parseRows(text) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const rows = [];
  for (const line of lines) {
    const parts = line.includes("\t") ? line.split("\t") : line.split(",");
    if (parts.length < 3) continue;
    const [item, unit, rate, category] = parts.map((p) => p.trim());
    if (!item || Number.isNaN(Number(rate))) continue; // skips a header row like "Item,Unit,Rate,Category"
    rows.push({ item, unit, rate: Number(rate), category: category || "Services" });
  }
  return rows;
}

export function BulkImportModal({ onClose, onImported }) {
  const [method, setMethod] = useState("paste");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const continueImport = async () => {
    if (method === "template") {
      // window.open() can't attach the Authorization header a Bearer-token (demo/localStorage)
      // partner session relies on — fetch the CSV via the authenticated axios instance instead
      // and trigger the download from an in-memory blob: URL.
      try {
        const res = await partnerWorkspaceApi.downloadTemplateBlob();
        const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = "njm_rate_card_template.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch {
        toast.error("Couldn't download the template — please try again.");
      }
      return;
    }
    if (method === "upload") {
      toast.error("File upload isn't available yet — use \"Paste from spreadsheet\" for now.");
      return;
    }
    const rows = parseRows(text);
    if (!rows.length) { toast.error("Paste at least one valid row (Item, Unit, Rate, Category)."); return; }
    setBusy(true);
    try {
      await partnerWorkspaceApi.bulkImport(rows);
      toast.success("Import queued — rows enter Pending NJM Approval");
      onImported();
    } catch {
      toast.error("Couldn't import — please check the format and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid="rate-card-bulk-import-modal">
      <div className="absolute inset-0" style={{ background: "var(--pw-scrim)" }} onClick={onClose} />
      <div className="relative w-full max-w-[640px] bg-[color:var(--pw-surface)] rounded-2xl pw-enter" style={{ boxShadow: "var(--pw-shadow-modal)" }}>
        <div className="flex items-start justify-between px-6 py-5 border-b" style={{ borderColor: "var(--pw-divider)" }}>
          <div className="pw-display text-[20px]">Bulk Import</div>
          <button onClick={onClose} data-testid="rate-card-bulk-import-close" className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[color:var(--pw-warm-tint)]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-2">
            {METHODS.map(({ key, title, description, icon: Icon, tag }) => (
              <button key={key} onClick={() => setMethod(key)} data-testid={`rate-card-bulk-import-method-${key}`}
                className="text-left rounded-xl p-3.5 border relative transition-colors"
                style={method === key ? { background: "var(--pw-yellow)", borderColor: "#F0E2AE" } : { background: "#FFFFFF", borderColor: "var(--pw-border)" }}>
                {method === key && <Check className="h-3.5 w-3.5 absolute top-3 right-3" style={{ color: "var(--pw-ink)" }} />}
                <Icon className="h-4 w-4 mb-2" style={{ color: method === key ? "var(--pw-ink)" : "var(--pw-text-secondary)" }} />
                <div className="text-[13px] font-bold mb-1">{title}</div>
                <div className="text-[11.5px]" style={{ color: method === key ? "#4A3D08" : "var(--pw-text-muted)" }}>{description}</div>
                {tag && <span className="pw-pill mt-2 inline-block" style={{ background: tag.bg, color: "#fff" }}>{tag.text}</span>}
              </button>
            ))}
          </div>
          {method === "paste" && (
            <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste your data here..." data-testid="rate-card-bulk-import-textarea"
              className="w-full pw-input px-3.5 py-3.5 text-[14px] min-h-[200px] mt-5" />
          )}
          <p className="text-[12px] mt-4" style={{ color: "var(--pw-text-faint)" }}>Parsing, column mapping &amp; file upload require backend support.</p>
        </div>
        <div className="px-6 py-4 border-t flex items-center justify-between" style={{ borderColor: "var(--pw-divider)" }}>
          <button onClick={onClose} className="pw-btn-secondary px-4 py-2.5 text-[13px]" data-testid="rate-card-bulk-import-cancel">Cancel</button>
          <button onClick={continueImport} disabled={busy} className="px-4 py-2.5 text-[13px] rounded-full font-bold" style={{ background: "var(--pw-ink)", color: "#fff" }} data-testid="rate-card-bulk-import-continue">Continue</button>
        </div>
      </div>
    </div>
  );
}
